import { Paperclip, Plus } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/occurrences/button'
import {
	Field,
	JustificationDialog,
	Notice,
	Panel,
} from '@/components/occurrences/occurrence-ui'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { APPROVERS, RESPONSIBLES } from '@/mocks/occurrences/catalog'
import { can, isActionOverdue } from '@/mocks/occurrences/rules'
import { newId, useOccurrenceMock } from '@/mocks/occurrences/store'
import type { ActionStatus, OccurrenceAction } from '@/types/occurrence'
import { ACTION_STATUS_LABEL } from '@/types/occurrence'
import { useOccurrence } from './occurrence-context'

const br = (d: string) => d.split('-').reverse().join('/')
const STATUS_TONE: Record<ActionStatus, string> = {
	OPEN: 'bg-kaefer-light-silver text-kaefer-dark-grey',
	IN_PROGRESS: 'bg-kaefer-astral-blue/15 text-kaefer-sapphire-blue',
	AWAITING_APPROVAL: 'bg-kaefer-violet/15 text-kaefer-violet',
	REJECTED: 'bg-kaefer-red/15 text-kaefer-red',
	DONE: 'bg-kaefer-grass-green/20 text-kaefer-forest-green',
	CANCELLED: 'bg-kaefer-silver/30 text-kaefer-dark-grey',
}

type Pending = {
	kind: 'reject' | 'cancel' | 'reassign' | 'extend'
	id: string
} | null

export const ActionsSection = () => {
	const { occ, profile, userName } = useOccurrence()
	const update = useOccurrenceMock((s) => s.update)
	const [openId, setOpenId] = useState<string | null>(null)
	const [creating, setCreating] = useState(false)
	const [pending, setPending] = useState<Pending>(null)
	const [newResp, setNewResp] = useState('')
	const [newDue, setNewDue] = useState('')
	const [form, setForm] = useState({
		description: '',
		origin: `Ocorrência #${occ.number}`,
		causeId: '',
		responsible: '',
		approver: APPROVERS[0],
		dueDate: '',
	})

	const closedLike = occ.status === 'CLOSED' || occ.status === 'CANCELLED'
	const manage = can(profile, 'manageActions') && !closedLike
	const roots = occ.investigation.causes.filter((c) => c.isRoot)
	const causeText = (id: string | null) =>
		occ.investigation.causes.find((c) => c.id === id)?.text ??
		'Sem causa vinculada'
	const open = occ.actions.find((a) => a.id === openId)

	const patchAction = (
		id: string,
		p: Partial<OccurrenceAction>,
		historyText: string,
		event: string,
	) =>
		update(
			occ.id,
			{
				actions: occ.actions.map((a) =>
					a.id === id
						? {
								...a,
								...p,
								history: [
									...a.history,
									{
										at: new Date().toISOString().slice(0, 16),
										by: userName,
										text: historyText,
									},
								],
							}
						: a,
				),
			},
			event,
			{ detail: historyText },
		)

	const create = () => {
		const a: OccurrenceAction = {
			id: newId(),
			description: form.description.trim(),
			origin: form.origin,
			causeId: form.causeId || null,
			responsible: form.responsible,
			approver: form.approver,
			dueDate: form.dueDate,
			status: 'OPEN',
			efficacy: null,
			evidence: [],
			history: [
				{
					at: new Date().toISOString().slice(0, 16),
					by: userName,
					text: 'Ação criada',
				},
			],
		}
		update(occ.id, { actions: [...occ.actions, a] }, 'Ação criada', {
			detail: a.description,
		})
		setCreating(false)
		setForm({ ...form, description: '', causeId: '', dueDate: '' })
	}
	const formValid =
		form.description.trim().length > 3 &&
		form.origin.trim() &&
		form.responsible &&
		form.dueDate

	return (
		<div className="space-y-4">
			<Panel
				title="Plano de ação"
				description="Ações vinculadas às causas da ocorrência ou independentes (visitas, inspeções). Origem obrigatória e rastreável."
				actions={
					manage && (
						<Button
							size="sm"
							variant="primary"
							className="gap-1.5"
							onClick={() => setCreating(true)}
						>
							<Plus className="size-4" /> Cadastrar ação
						</Button>
					)
				}
			>
				{occ.actions.length === 0 ? (
					<p className="text-sm text-muted-foreground">
						Nenhuma ação cadastrada.
					</p>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full min-w-[760px] text-sm">
							<thead>
								<tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
									<th className="py-2 pr-2">#</th>
									<th className="pr-2">Causa</th>
									<th className="pr-2">Ação</th>
									<th className="pr-2">Responsável</th>
									<th className="pr-2">Prazo</th>
									<th className="pr-2">Status</th>
									<th>Eficácia</th>
								</tr>
							</thead>
							<tbody>
								{occ.actions.map((a, i) => {
									const overdue = isActionOverdue(a.dueDate, a.status)
									return (
										<tr
											key={a.id}
											className="cursor-pointer border-b align-top last:border-0 hover:bg-muted/30"
											onClick={() => setOpenId(a.id)}
										>
											<td className="py-2.5 pr-2 text-xs text-muted-foreground">
												{i + 1}
											</td>
											<td className="max-w-48 pr-2 text-xs text-muted-foreground">
												{causeText(a.causeId)}
											</td>
											<td className="max-w-80 pr-2">
												{a.description}
												{a.evidence.length > 0 && (
													<Paperclip className="ml-1 inline size-3 text-kaefer-pumpkin-orange" />
												)}
												<p className="text-[11px] text-muted-foreground">
													Origem: {a.origin}
												</p>
											</td>
											<td className="pr-2 text-xs">{a.responsible}</td>
											<td
												className={cn(
													'pr-2 text-xs',
													overdue && 'font-semibold text-kaefer-red',
												)}
											>
												{br(a.dueDate)}
												{overdue && ' · vencida'}
											</td>
											<td className="pr-2">
												<span
													className={cn(
														'rounded-full px-2 py-0.5 text-[11px] font-medium',
														STATUS_TONE[a.status],
													)}
												>
													{ACTION_STATUS_LABEL[a.status]}
												</span>
											</td>
											<td className="text-xs">
												{a.efficacy === 'EFFECTIVE'
													? 'Eficaz'
													: a.efficacy === 'INEFFECTIVE'
														? 'Ineficaz'
														: '—'}
											</td>
										</tr>
									)
								})}
							</tbody>
						</table>
					</div>
				)}
			</Panel>

			{/* Detalhe / workflow da ação */}
			<Dialog open={Boolean(open)} onOpenChange={(v) => !v && setOpenId(null)}>
				<DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl">
					{open &&
						(() => {
							const isExecutor = open.responsible === userName
							const canApprove = can(profile, 'approveAction') && !isExecutor
							const overdue = isActionOverdue(open.dueDate, open.status)
							return (
								<>
									<DialogHeader>
										<DialogTitle>
											Ação · {ACTION_STATUS_LABEL[open.status]}
											{overdue && ' · vencida'}
										</DialogTitle>
										<DialogDescription>
											Causa: {causeText(open.causeId)} · Origem: {open.origin}
										</DialogDescription>
									</DialogHeader>
									<div className="space-y-3 text-sm">
										<p className="rounded bg-muted/30 p-2">
											{open.description}
										</p>
										<div className="grid grid-cols-3 gap-2 text-xs">
											<span>
												<b>Responsável:</b>
												<br />
												{open.responsible}
											</span>
											<span>
												<b>Aprovador:</b>
												<br />
												{open.approver}
											</span>
											<span>
												<b>Prazo:</b>
												<br />
												{br(open.dueDate)}
											</span>
										</div>
										<div>
											<p className="text-xs font-medium uppercase text-muted-foreground">
												Evidências
											</p>
											{open.evidence.length === 0 ? (
												<p className="text-xs text-muted-foreground">
													Nenhuma. A conclusão exige evidência.
												</p>
											) : (
												<ul className="text-xs">
													{open.evidence.map((e) => (
														<li key={e}>
															<Paperclip className="mr-1 inline size-3" />
															{e}
														</li>
													))}
												</ul>
											)}
											{manage &&
												open.status !== 'DONE' &&
												open.status !== 'CANCELLED' && (
													<Button
														size="sm"
														variant="outline"
														className="mt-1 gap-1"
														onClick={() =>
															patchAction(
																open.id,
																{
																	evidence: [
																		...open.evidence,
																		`evidencia-${open.evidence.length + 1}.jpg`,
																	],
																},
																'Evidência anexada',
																'Evidência de ação anexada',
															)
														}
													>
														<Paperclip className="size-3.5" /> Anexar evidência
													</Button>
												)}
										</div>

										{/* Workflow */}
										{manage && (
											<div className="flex flex-wrap gap-2 border-t pt-3">
												{open.status === 'OPEN' && (
													<Button
														size="sm"
														variant="primary"
														onClick={() =>
															patchAction(
																open.id,
																{ status: 'IN_PROGRESS' },
																'Execução iniciada',
																'Ação iniciada',
															)
														}
													>
														Iniciar execução
													</Button>
												)}
												{(open.status === 'IN_PROGRESS' ||
													open.status === 'REJECTED') && (
													<Button
														size="sm"
														variant="primary"
														disabled={open.evidence.length === 0}
														title={
															open.evidence.length === 0
																? 'Anexe uma evidência'
																: ''
														}
														onClick={() =>
															patchAction(
																open.id,
																{ status: 'AWAITING_APPROVAL' },
																'Concluída e enviada para aprovação',
																'Ação enviada para aprovação',
															)
														}
													>
														Concluir e enviar para aprovação
													</Button>
												)}
												{open.status === 'AWAITING_APPROVAL' && (
													<>
														<Button
															size="sm"
															variant="primary"
															disabled={!canApprove}
															onClick={() =>
																patchAction(
																	open.id,
																	{ status: 'DONE' },
																	'Aprovada',
																	'Ação aprovada',
																)
															}
														>
															Aprovar
														</Button>
														<Button
															size="sm"
															variant="destructive"
															disabled={!canApprove}
															onClick={() =>
																setPending({ kind: 'reject', id: open.id })
															}
														>
															Reprovar
														</Button>
													</>
												)}
												{!['DONE', 'CANCELLED'].includes(open.status) && (
													<>
														<Button
															size="sm"
															variant="outline"
															onClick={() => {
																setNewResp(open.responsible)
																setPending({ kind: 'reassign', id: open.id })
															}}
														>
															Alterar responsável
														</Button>
														<Button
															size="sm"
															variant="outline"
															onClick={() => {
																setNewDue(open.dueDate)
																setPending({ kind: 'extend', id: open.id })
															}}
														>
															Prorrogar prazo
														</Button>
														<Button
															size="sm"
															variant="outline"
															onClick={() =>
																setPending({ kind: 'cancel', id: open.id })
															}
														>
															Cancelar
														</Button>
													</>
												)}
											</div>
										)}
										{open.status === 'AWAITING_APPROVAL' && isExecutor && (
											<Notice tone="warn">
												Segregação de funções: o executor não pode aprovar a
												própria ação.
											</Notice>
										)}
										{open.status === 'AWAITING_APPROVAL' &&
											!isExecutor &&
											!can(profile, 'approveAction') && (
												<Notice>
													Apenas Gestor/Gestor SSMA do escopo aprovam ações.
												</Notice>
											)}

										{/* Eficácia: por ação, depois consolidada no encerramento */}
										{open.status === 'DONE' && (
											<div className="space-y-2 border-t pt-3">
												<p className="text-xs font-medium uppercase text-muted-foreground">
													Avaliação de eficácia
												</p>
												{open.efficacy ? (
													<p className="text-sm">
														{open.efficacy === 'EFFECTIVE'
															? 'Eficaz'
															: 'Ineficaz'}{' '}
														— {open.efficacyNote}
													</p>
												) : null}
												{can(profile, 'concludeInvestigation') &&
													!closedLike && (
														<EfficacyForm
															onSave={(e, n) =>
																patchAction(
																	open.id,
																	{ efficacy: e, efficacyNote: n },
																	`Eficácia: ${e === 'EFFECTIVE' ? 'Eficaz' : 'Ineficaz'} — ${n}`,
																	'Eficácia avaliada',
																)
															}
														/>
													)}
											</div>
										)}

										<div className="border-t pt-3">
											<p className="text-xs font-medium uppercase text-muted-foreground">
												Histórico
											</p>
											<ul className="mt-1 space-y-1 text-xs">
												{[...open.history].reverse().map((h, i) => (
													<li key={i}>
														{h.at.replace('T', ' ')} · {h.by} · {h.text}
													</li>
												))}
											</ul>
										</div>
									</div>
								</>
							)
						})()}
				</DialogContent>
			</Dialog>

			{/* Justificativas */}
			<JustificationDialog
				open={pending?.kind === 'reject'}
				onOpenChange={(v) => !v && setPending(null)}
				title="Reprovar ação"
				danger
				confirmLabel="Reprovar"
				onConfirm={(j) =>
					pending &&
					patchAction(
						pending.id,
						{ status: 'REJECTED' },
						`Reprovada: ${j}`,
						'Ação reprovada',
					)
				}
			/>
			<JustificationDialog
				open={pending?.kind === 'cancel'}
				onOpenChange={(v) => !v && setPending(null)}
				title="Cancelar ação"
				danger
				confirmLabel="Cancelar ação"
				onConfirm={(j) =>
					pending &&
					patchAction(
						pending.id,
						{ status: 'CANCELLED' },
						`Cancelada: ${j}`,
						'Ação cancelada',
					)
				}
			/>
			<Dialog
				open={pending?.kind === 'reassign' || pending?.kind === 'extend'}
				onOpenChange={(v) => !v && setPending(null)}
			>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>
							{pending?.kind === 'reassign'
								? 'Alterar responsável'
								: 'Prorrogar prazo'}
						</DialogTitle>
						<DialogDescription>
							Exige justificativa e fica no histórico.
						</DialogDescription>
					</DialogHeader>
					<ChangeForm
						kind={pending?.kind === 'reassign' ? 'reassign' : 'extend'}
						value={pending?.kind === 'reassign' ? newResp : newDue}
						onValue={pending?.kind === 'reassign' ? setNewResp : setNewDue}
						onConfirm={(j) => {
							if (!pending) return
							if (pending.kind === 'reassign')
								patchAction(
									pending.id,
									{ responsible: newResp },
									`Responsável alterado para ${newResp}: ${j}`,
									'Responsável alterado',
								)
							else
								patchAction(
									pending.id,
									{ dueDate: newDue },
									`Prazo prorrogado para ${br(newDue)}: ${j}`,
									'Prazo prorrogado',
								)
							setPending(null)
						}}
					/>
				</DialogContent>
			</Dialog>

			{/* Nova ação */}
			<Dialog open={creating} onOpenChange={setCreating}>
				<DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-xl">
					<DialogHeader>
						<DialogTitle>Cadastrar ação</DialogTitle>
					</DialogHeader>
					<div className="space-y-3">
						<Field
							label="Causa"
							hint="Opcional: ações independentes podem ter outra origem"
						>
							<NativeSelect
								value={form.causeId}
								onChange={(e) => setForm({ ...form, causeId: e.target.value })}
							>
								<NativeSelectOption value="">
									Sem causa vinculada
								</NativeSelectOption>
								{roots.map((c) => (
									<NativeSelectOption key={c.id} value={c.id}>
										{c.text}
									</NativeSelectOption>
								))}
							</NativeSelect>
						</Field>
						<Field label="Origem" required>
							<NativeSelect
								value={form.origin}
								onChange={(e) => setForm({ ...form, origin: e.target.value })}
							>
								{[
									`Ocorrência #${occ.number}`,
									'Visita técnica',
									'Inspeção',
									'Auditoria',
									'Outra fonte autorizada',
								].map((o) => (
									<NativeSelectOption key={o}>{o}</NativeSelectOption>
								))}
							</NativeSelect>
						</Field>
						<Field label="Descrição da ação" required>
							<Textarea
								rows={3}
								value={form.description}
								onChange={(e) =>
									setForm({ ...form, description: e.target.value })
								}
							/>
						</Field>
						<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
							<Field label="Responsável" required>
								<NativeSelect
									value={form.responsible}
									onChange={(e) =>
										setForm({ ...form, responsible: e.target.value })
									}
								>
									<NativeSelectOption value="">Selecione</NativeSelectOption>
									{RESPONSIBLES.map((r) => (
										<NativeSelectOption key={r}>{r}</NativeSelectOption>
									))}
								</NativeSelect>
							</Field>
							<Field label="Aprovador" required>
								<NativeSelect
									value={form.approver}
									onChange={(e) =>
										setForm({ ...form, approver: e.target.value })
									}
								>
									{APPROVERS.map((r) => (
										<NativeSelectOption key={r}>{r}</NativeSelectOption>
									))}
								</NativeSelect>
							</Field>
							<Field label="Prazo" required>
								<Input
									type="date"
									value={form.dueDate}
									onChange={(e) =>
										setForm({ ...form, dueDate: e.target.value })
									}
								/>
							</Field>
						</div>
						{form.responsible === form.approver && (
							<Notice tone="warn">
								Responsável e aprovador iguais: conflito de interesse. Escolha
								pessoas diferentes.
							</Notice>
						)}
					</div>
					<div className="flex justify-end gap-2">
						<Button variant="cancel" onClick={() => setCreating(false)}>
							Cancelar
						</Button>
						<Button
							variant="primary"
							disabled={!formValid || form.responsible === form.approver}
							onClick={create}
						>
							Cadastrar
						</Button>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	)
}

const EfficacyForm = ({
	onSave,
}: {
	onSave: (e: 'EFFECTIVE' | 'INEFFECTIVE', note: string) => void
}) => {
	const [note, setNote] = useState('')
	return (
		<div className="space-y-2">
			<Textarea
				rows={2}
				value={note}
				onChange={(e) => setNote(e.target.value)}
				placeholder="Como a eficácia foi verificada?"
			/>
			<div className="flex gap-2">
				<Button
					size="sm"
					variant="primary"
					disabled={note.trim().length < 5}
					onClick={() => onSave('EFFECTIVE', note)}
				>
					Eficaz
				</Button>
				<Button
					size="sm"
					variant="destructive"
					disabled={note.trim().length < 5}
					onClick={() => onSave('INEFFECTIVE', note)}
				>
					Ineficaz
				</Button>
			</div>
		</div>
	)
}

const ChangeForm = ({
	kind,
	value,
	onValue,
	onConfirm,
}: {
	kind: 'reassign' | 'extend'
	value: string
	onValue: (v: string) => void
	onConfirm: (j: string) => void
}) => {
	const [j, setJ] = useState('')
	return (
		<div className="space-y-3">
			{kind === 'reassign' ? (
				<NativeSelect value={value} onChange={(e) => onValue(e.target.value)}>
					{RESPONSIBLES.map((r) => (
						<NativeSelectOption key={r}>{r}</NativeSelectOption>
					))}
				</NativeSelect>
			) : (
				<Input
					type="date"
					value={value}
					onChange={(e) => onValue(e.target.value)}
				/>
			)}
			<Textarea
				rows={3}
				value={j}
				onChange={(e) => setJ(e.target.value)}
				placeholder="Justificativa"
			/>
			<div className="flex justify-end">
				<Button
					variant="primary"
					disabled={j.trim().length < 5}
					onClick={() => onConfirm(j.trim())}
				>
					Confirmar
				</Button>
			</div>
		</div>
	)
}
